/**
 * 应用角标（任务 18a 口径，18c1 从 pushCapability 拆出）。
 *
 * **为什么单独一个文件**：角标接线（store/appBadge）在首屏链上，而 pushCapability
 * 依赖 installGuideModel（整页安装引导文案）——放在一起时那份文案会被整包带进首屏
 * bundle（实测 +2.5KB gzip）。这里不依赖任何 UA/能力判定，只有纯计算与一次写入。
 * 能力分档（canBadgeIn 等）仍在 pushCapability。
 */
import type { ConversationRecord } from '~/worker/contract';

/**
 * 应用角标数 = **非免打扰**会话的未读之和（含归档会话——它们也是未读）。
 *
 * 与通知过滤同口径：免打扰会话既不弹通知也不计角标。旧版两处都和这不一样
 * （legacy §5：全局角标按「当前筛选视图」求和，切 chip 角标就变；撤回/系统通知
 * 也计未读）——新版未读本身已由 `msgTypeSpec().countsUnread` 把控。
 */
export function badgeCountOf(rows: readonly ConversationRecord[] | null): number {
  if (rows === null) return 0;
  let total = 0;
  for (const row of rows) {
    if (row.remote.noDisturbing === 0) total += row.remote.unread;
  }
  return total;
}

/** `navigator` 的角标面（可注入） */
export interface BadgeNavigator {
  readonly setAppBadge?: (count?: number) => Promise<void>;
  readonly clearAppBadge?: () => Promise<void>;
}

/**
 * 写角标：0 走 clearAppBadge（传 0 给 setAppBadge 在部分实现里是「显示圆点」而非清除）。
 * 失败一律静默——未授权的 iOS 会 reject，不支持的浏览器没这方法，角标是锦上添花。
 */
export function applyAppBadge(nav: BadgeNavigator, count: number): void {
  try {
    const ret = count > 0 ? nav.setAppBadge?.(count) : nav.clearAppBadge?.();
    if (ret !== undefined) void ret.catch(() => undefined);
  } catch {
    // 同步抛错（老实现/被策略拦）：忽略
  }
}
